import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import { useLookup } from '@unifyapps/network/generated/lookup-rest-api/lookup-rest-api';
import type { LookupByKeysRequest } from '@unifyapps/network/generated/models/lookupByKeysRequest';
import { LookupByKeysRequestType } from '@unifyapps/network/generated/models/lookupByKeysRequestType';
import type { Notification as NotificationType } from '@unifyapps/network/generated/models/notification';
import _uniq from 'lodash/uniq';
import { useMemo } from 'react';
import type { Notification, NotificationEventTypesLookupResponse } from '../types';
import { useNotificationCategories } from './useNotificationCategories';

export const useResolvedNotifications = ({
  notifications,
}: {
  notifications: NotificationType[] | undefined;
}): { notifications: Notification[] | undefined } => {
  const { data: notificationEventTypes, isLoading: areNotificationEventTypesLoading } =
    useLookup<NotificationEventTypesLookupResponse>(
      {
        type: LookupByKeysRequestType.ByKeys,
        lookupType: 'ENTITY',
        keys: _uniq(
          notifications?.map((notification) => notification.notificationEventType).sort(),
        ),
        options: {
          entity_type: UAEntityTypes.NotificationEventType,
        },
      } as LookupByKeysRequest,
      {
        query: {
          enabled: Boolean(notifications?.length),
        },
      },
    );

  const { categories, isLoading: isNotificationCategoriesLoading } = useNotificationCategories();

  const resolvedNotifications = useMemo(() => {
    if (
      areNotificationEventTypesLoading ||
      isNotificationCategoriesLoading ||
      !notificationEventTypes ||
      !categories
    ) {
      return notifications;
    }

    return notifications?.map((notification) => {
      /**
       * Return the notification object as it is if:
       * 1. notificationEventType is not present
       * 2. notificationEventTypes lookup is still loading or call failed or object for this value is not present in lookup
       */
      if (
        !notification.notificationEventType ||
        !notificationEventTypes.response?.objects?.[notification.notificationEventType]?.properties
      ) {
        return notification;
      }

      // Get the category value for this particular notificationEventType
      const category =
        notificationEventTypes.response.objects[notification.notificationEventType].properties
          .category;

      return {
        ...notification,
        // Lookup the category value and add the resolved object in notification.category
        category: categories[category],
      };
    });
  }, [
    isNotificationCategoriesLoading,
    areNotificationEventTypesLoading,
    categories,
    notificationEventTypes,
    notifications,
  ]);

  return { notifications: resolvedNotifications as Notification[] };
};
